import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useSupabase } from '@/lib/supabase-context'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import { cuentasService } from '@/services/cuentasService'
import type { Database } from '@/types/database'

type CuentaInsert = Database['public']['Tables']['cuentas']['Insert']
type CuentaUpdate = Database['public']['Tables']['cuentas']['Update']

// Query keys
export const cuentasKeys = {
  all: ['cuentas'] as const,
  lists: () => [...cuentasKeys.all, 'list'] as const,
  list: (incluirInactivas?: boolean) => [...cuentasKeys.lists(), { incluirInactivas }] as const,
  stats: (id: string, organizacionId?: string | null) => [...cuentasKeys.all, 'stats', id, organizacionId] as const,
}

// Hook para listar cuentas con saldo
export function useCuentasConSaldo(incluirInactivas = false) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  const { organizacionId } = useOrganizacion()

  return useQuery({
    queryKey: [...cuentasKeys.list(incluirInactivas), 'conSaldo', organizacionId] as const,
    queryFn: () => {
      if (!organizacionId) throw new Error('No organization')
      return cuentasService.listarConSaldo(supabase!, organizacionId, incluirInactivas)
    },
    enabled: !!supabase && !supabaseLoading && !!organizacionId,
  })
}

// Hook para obtener estadísticas de una cuenta
export function useCuentaEstadisticas(id: string) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  const { organizacionId } = useOrganizacion()

  return useQuery({
    queryKey: cuentasKeys.stats(id, organizacionId),
    queryFn: () => {
      if (!organizacionId) throw new Error('No organization')
      return cuentasService.obtenerEstadisticas(supabase!, id, organizacionId)
    },
    enabled: !!supabase && !supabaseLoading && !!id && !!organizacionId,
  })
}

// Hook para crear cuenta
export function useCrearCuenta() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()

  return useMutation({
    mutationFn: (cuenta: CuentaInsert) => cuentasService.crear(supabase!, cuenta),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cuentasKeys.lists() })
    },
  })
}

// Hook para actualizar cuenta
export function useActualizarCuenta() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CuentaUpdate }) =>
      cuentasService.actualizar(supabase!, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cuentasKeys.lists() })
    },
  })
}

// Hook para eliminar cuenta
export function useEliminarCuenta() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()

  return useMutation({
    mutationFn: (id: string) => cuentasService.eliminar(supabase!, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cuentasKeys.lists() })
    },
  })
}

// Hook para toggle activa/inactiva
export function useToggleCuentaActiva() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()

  return useMutation({
    mutationFn: ({ id, activa }: { id: string; activa: boolean }) =>
      cuentasService.toggleActiva(supabase!, id, activa),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cuentasKeys.lists() })
    },
  })
}
